import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { useMatch } from 'react-router-dom';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { So5FixtureEvent } from '@sorare/core/src/__generated__/globalTypes';
import { StyledLinkBox } from '@sorare/core/src/atoms/layout/Accordion';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BrandS, BrandXXS, LabelM } from '@sorare/core/src/atoms/typography';
import { Ellipsis } from '@sorare/core/src/atoms/typography/Ellipsis';
import { Avatar } from '@sorare/core/src/components/user/Avatar';
import { playerDetails } from '@sorare/core/src/lib/glossary';

import { CardsLeft } from 'components/leaderboard/CardsLeft';

import { CumulativeRankRow } from '../CumulativeRankRow';
import { PastLeaderboardList } from '../PastLeaderboardList';
import { RankRow, getRankRowState } from '../RankRow';
import type { CumulativeRanking_So5UserGroupMembership } from './__generated__/index.graphql';

const Root = styled(SBHorizontal)`
  width: 100%;
  padding-right: var(--intermediate-unit);
`;
const AvatarContainer = styled(Horizontal).attrs({ gap: 0, center: true })`
  --avatar-size: 40px;
  width: var(--avatar-size);
  height: var(--avatar-size);
  border-radius: 50%;
  background-color: var(--c-nd-400);

  & > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

type Props = {
  so5Ranking: CumulativeRanking_So5UserGroupMembership;
  isMyRanking?: boolean;
  userGroupSlug: string;
  activeLineupId?: string;
  withCollapsibleRanking?: boolean;
  rankingLink?: (
    fixtureSlug: string,
    userGroupSlug: string,
    userSlug: string,
    eventType?: So5FixtureEvent
  ) => string;
};
export const CumulativeRanking = ({
  so5Ranking,
  isMyRanking,
  userGroupSlug,
  activeLineupId,
  withCollapsibleRanking,
  rankingLink,
}: Props) => {
  const { user, lastSo5Rankings } = so5Ranking;

  const lastSo5Ranking = lastSo5Rankings?.find(Boolean);
  const lastFixture = lastSo5Ranking?.so5Fixture;

  const rankingRoute =
    rankingLink?.(
      lastFixture?.slug ?? '',
      userGroupSlug,
      user.slug,
      lastFixture?.type
    ) ?? '';

  const matchRoute = useMatch(rankingRoute);
  const isFocusedRanking = !!rankingRoute && matchRoute;

  return withCollapsibleRanking ? (
    <CumulativeRankRow
      isMe={isMyRanking}
      isSelected={!!isFocusedRanking}
      rank={so5Ranking.ranking}
      title={
        <Horizontal gap={2}>
          <AvatarContainer>
            <Avatar user={user} size={5} rounded />
          </AvatarContainer>
          <Vertical gap={0}>
            <Vertical gap={0.5}>
              <LabelM bold>
                <Ellipsis>{user.nickname}</Ellipsis>
              </LabelM>
            </Vertical>
            <Horizontal gap={0.5}>
              <BrandS as="span" color="var(--c-white)">
                <FormattedNumber value={so5Ranking.score} />
              </BrandS>
              <BrandXXS color="var(--c-nd-600)" as="span">
                <FormattedMessage {...playerDetails.points} />
              </BrandXXS>
            </Horizontal>
          </Vertical>
        </Horizontal>
      }
    >
      {({ expanded }) => (
        <PastLeaderboardList
          expanded={expanded}
          userSlug={user.slug}
          userGroupSlug={userGroupSlug}
          activeLineupId={activeLineupId}
        />
      )}
    </CumulativeRankRow>
  ) : (
    <StyledLinkBox
      as={Link}
      $noTopPadding
      $noHorizontalPadding
      $isFilter={false}
      to={
        rankingLink?.(
          lastFixture?.slug ?? '',
          userGroupSlug,
          isFocusedRanking ? '' : user.slug,
          lastFixture?.type
        ) ?? ''
      }
    >
      <RankRow
        state={getRankRowState(
          !!isMyRanking,
          !!isFocusedRanking,
          so5Ranking.ranking
        )}
        rank={so5Ranking.ranking}
      >
        <Root>
          <Horizontal gap={1.5}>
            <AvatarContainer>
              <Avatar user={user} size={5} rounded />
            </AvatarContainer>
            <Vertical gap={0}>
              <LabelM bold>
                <Ellipsis>{user.nickname}</Ellipsis>
              </LabelM>
              <Horizontal gap={0.5}>
                <BrandS as="span" color="var(--c-white)">
                  <FormattedNumber value={so5Ranking.score} />
                </BrandS>
                <BrandXXS color="var(--c-nd-600)" as="span">
                  <FormattedMessage {...playerDetails.points} />
                </BrandXXS>
              </Horizontal>
            </Vertical>
          </Horizontal>
          <CardsLeft so5Lineup={lastSo5Ranking?.so5Lineup} />
        </Root>
      </RankRow>
    </StyledLinkBox>
  );
};

CumulativeRanking.fragments = {
  So5UserGroupMembership: gql`
    fragment CumulativeRanking_So5UserGroupMembership on So5UserGroupMembership {
      id
      ranking
      score
      lastSo5Rankings(size: 10) {
        id
        so5Lineup {
          id
          ...CardsLeft_so5Lineup
        }
        so5Fixture {
          slug
          type
        }
      }
      user {
        id
        slug
        nickname
        ...Avatar_publicUserInfoInterface
      }
    }
    ${Avatar.fragments.publicUserInfoInterface}
    ${CardsLeft.fragments.so5Lineup}
  ` as TypedDocumentNode<CumulativeRanking_So5UserGroupMembership>,
};
